import { ECONOMIC_TRANSFER_DIALOG } from '../../core/http-interceptors/economic-transfer-context';
import { EconomicTransferErrorComponent, EconomicTransferError } from '../../shared/economic-transfer-error/economic-transfer-error.component';
import { Injectable } from '@angular/core';
import { InvoicePdfService } from '../../invoices/services/invoice-pdf.service';
import { FakturaService } from '../../invoices/services/faktura.service';
import { BehaviorSubject, combineLatest, EMPTY, from } from 'rxjs';
import { catchError, finalize, first, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { ClientOptionsSelectors } from '../../clients/client-details/store/services';
import { AuthService } from '../../auth/store/services';
import pdfMake from 'pdfmake/build/pdfmake';
import { Settings } from '../../auth/store/models';
import { FinanceChargeTerms } from '../../clients/client-details/store/models';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProgressModalComponent } from '../../shared/progress-modal/progress-modal.component';
import { AlertService } from '../../core/alerts/alert.service';

interface InvoiceToEconomic {
    fakturanr: number;
    glkundenr: number;
    totalbel: number;
    valuta: string;
    dato: string;
    betdato: string;
    _Data: any;
}

@Injectable({
    providedIn: 'root'
})
export class EconomicService {
    progressMessageSubject = new BehaviorSubject<string>('Overfører til Economic');
    progressMessage$ = this.progressMessageSubject.asObservable();

    constructor(
        private invoicePdfService: InvoicePdfService,
        private fakturaService: FakturaService,
        private authService: AuthService,
        private clientOptionsSelector: ClientOptionsSelectors,
        private http: HttpClient,
        private ngbModal: NgbModal,
        private alert: AlertService,
    ) { }

    timeposterToEconomic() {
        const progressModal = this.ngbModal.open(ProgressModalComponent, { size: 'lg', backdrop: 'static', keyboard: false });
        progressModal.componentInstance.title = 'Timeposter til Economic';
        progressModal.componentInstance.progressMessage$ = this.progressMessage$;
        let transferStarted = false;
        combineLatest([
            this.getTimeposterFakturanumreToTransfer(),
            this.authService.getOfficeSettings(),
            this.clientOptionsSelector.financeChargeTerms.pipe(first()),
        ]).pipe(
            switchMap(([fakturanumre, officeSettings, financeChargeTerms]) => from(fakturanumre).pipe(
                mergeMap(fakturanr => from(this.createInvoiceToEconomic(fakturanr, officeSettings, financeChargeTerms))),
            )),
            toArray(),
            switchMap(invoices => {
                transferStarted = true;
                return this.toEconomicWithInvoices(invoices.filter(i => i !== undefined));
            }),
            map(() => this.alert.createAlert(`Timeposter overført til Economic!`, 'success')),
            catchError((error: unknown) => {
                progressModal.close();
                // Preparation requests retain their existing global error handling.
                const httpError = error instanceof HttpErrorResponse ? error : undefined;
                if ((transferStarted || !httpError) && httpError?.status !== 401) {
                    const body = httpError?.error;
                    const structured = body?.code === 'economic_transfer_failed';
                    const category = structured && ['rejected', 'partial', 'unknown', 'failed'].includes(body.category)
                        ? body.category : transferStarted && (httpError?.status === 0 || (httpError?.status ?? 0) >= 500) ? 'unknown' : 'failed';
                    const failure: EconomicTransferError = {
                        category,
                        message: structured && typeof body.message === 'string' ? body.message
                            : typeof body === 'string' && !body.trimStart().startsWith('<') ? body.slice(0, 16000)
                            : !transferStarted ? `Overførslen blev ikke startet.${error instanceof Error && error.message ? ` ${error.message}` : ' Forberedelsen mislykkedes.'}`
                            : 'Overførslen kunne ikke bekræftes. Kontrollér resultatet i e-conomic.',
                        responseDetails: structured ? body.responseDetails : undefined,
                        stage: structured ? body.stage : !transferStarted ? 'preparation' : undefined,
                        upstreamStatus: structured ? body.upstreamStatus : undefined,
                        attachmentsCompleted: structured ? body.attachmentsCompleted : undefined,
                        attachmentInvoiceNumber: structured ? body.attachmentInvoiceNumber : undefined,
                        attachmentVoucherNumber: structured ? body.attachmentVoucherNumber : undefined,
                        attachmentFileSize: structured ? body.attachmentFileSize : undefined,
                        attachmentAccountingYear: structured ? body.attachmentAccountingYear : undefined,
                    };
                    const modal = this.ngbModal.open(EconomicTransferErrorComponent, { size: 'lg' });
                    modal.componentInstance.error = failure;
                }
                return EMPTY;
            }),
            finalize(() => progressModal.close()),
        ).subscribe();
    }

    private toEconomicWithInvoices(invoices: InvoiceToEconomic[]) {
        this.progressMessageSubject.next('Overfører poster og fakturaer til Economic');
        const url = `[api]/Timeposter/toEconomic/[officeId]`;
        return this.http.post(url, invoices, { context: new HttpContext().set(ECONOMIC_TRANSFER_DIALOG, true) });
    }

    private getTimeposterFakturanumreToTransfer() {
        const url = `[api]/Timeposter/notTransferredFakturanumreByOffice/[officeId]`;
        return this.http.get<number[]>(url);
    }

    private async getBase64Data(doc: any): Promise<string> {
        const pdfDocGenerator = pdfMake.createPdf(doc);
        return new Promise<string>((resolve) => {
            pdfDocGenerator.getBase64(data => {
                resolve(data);
            });
        });
    }

    private async createInvoiceToEconomic(fakturanr: number, officeSettings: Settings, financeChargeTerms: FinanceChargeTerms[]): Promise<InvoiceToEconomic> {
        const faktura = await this.fakturaService.fakturaByFakturanr(fakturanr).toPromise();
        this.progressMessageSubject.next(`Henter faktura: ${fakturanr}`);
        if (!faktura) { return; }
        const interestRate = financeChargeTerms.find(term => term.code === faktura.kunder.navRentebetingelser)?.interestRate;
        const doc = this.invoicePdfService.getPDFdoc(faktura, officeSettings, interestRate);
        const base64Data = await this.getBase64Data(doc);
        return {
            fakturanr: faktura.fakturanr,
            totalbel: faktura.totalbel,
            valuta: faktura.valuta,
            glkundenr: faktura.kunder.glkundenr,
            dato: faktura.dato,
            betdato: faktura.betdato,
            _Data: base64Data,
        } as InvoiceToEconomic;
    }
}
